import { z } from "zod";

/**
 * Ship a finished compression: the four-level CDT the studio produced from its
 * seven-level draft, written to the merchflow's own decision tree.
 *
 * The payload carries BOTH the shipped assignments and the draft they came
 * from, because the release record is only useful if it is reproducible —
 * re-run the compression over the stored draft and the stored contract must
 * come back unchanged. Sending the assignments alone would give a tree nobody
 * can explain a month later.
 *
 * REFUSALS. 200 returns `ResponsePostMerchflowCdtCompression`; a 422 returns
 * `CdtCompressionRejection` (at the foot of this file), which is the only
 * structured refusal this route emits and the shape a consumer must handle to
 * report an unmatched-universe or unplaced-combo failure. Every other status
 * is the plain `ApiError` from `api/apiError.ts`.
 */
export const RoutePostMerchflows_merchflowId_CdtCompression =
  "/merchflows/:merchflowId/cdt/compression";

/**
 * This route's own params. Structurally identical to
 * `ParamsMerchflowCdtDraftSchema` today, and deliberately not a re-export of
 * it: a named schema per route is what lets one route's params change without
 * silently changing the other's. The server wires THIS schema onto the POST.
 */
export const ParamsMerchflowCdtCompressionSchema = z.object({
  merchflowId: z.coerce.number().int().positive(),
});

/**
 * One product's shipped CDT. Values are the compound labels the compression
 * produced, already sanitised and trimmed here so they cannot disagree with
 * `cdt0Order` over a level-0 name that differs only in whitespace.
 *
 * CDT0 AND CDT1 ARE NON-EMPTY. They are the vertical blocking pair, and an
 * empty one is not a shallower tree — it is data loss. An empty `cdt0` creates
 * no level-0 node, so the item is written with `node_id = null`: silently
 * unassigned, absent from `unmatchedProductCodes`, and still counted in
 * `itemCount`, so the studio reports success over a hole. An empty `cdt1`
 * parks the product on its CDT0 node with no CDT1 depth, which is the shape of
 * the `Unassigned CDT1` placeholder the solver must never be handed. This
 * schema is the only place either can become a 400 instead of quiet loss.
 *
 * CDT2 AND CDT3 MAY BE EMPTY, and empty is their meaning: the branch stops
 * here. The collapse never pads up to four levels — a spine with two or three
 * distinguishing levels emits two or three values and the rest arrive `""` —
 * and the server reads that on purpose: it skips any node whose path has an
 * empty part and places the item on the deepest level that does exist. Empty
 * string is the encoding because there is no absence to encode: every
 * assignment carries all four keys, always.
 */
export const CdtCompressionAssignmentSchema = z.object({
  productCode: z.string().min(1),
  cdt0: z.string().trim().min(1),
  cdt1: z.string().trim().min(1),
  cdt2: z.string().trim(),
  cdt3: z.string().trim(),
  cdt1Priority: z.number().int().min(0),
  cdt2Priority: z.number().int().min(0),
  cdt3Priority: z.number().int().min(0),
});

export const CdtRuleCarrierSchema = z.enum([
  "rowHome",
  "lossy",
  "cdt2",
  "ladderCdt2",
  "cdt3",
]);

export interface CdtRuleTreeKid {
  value: string;
  count: number;
  node: CdtRuleTreeNode | null;
}

export interface CdtRuleTreeNode {
  attr: string;
  carrier: z.infer<typeof CdtRuleCarrierSchema>;
  describe: string;
  direction: string;
  ladderGroups?: Record<string, string>;
  kids: CdtRuleTreeKid[];
}

/** Recursive decision tree measured for one base-Pog CDT0/CDT1 pair. */
export const CdtRuleTreeNodeSchema: z.ZodType<CdtRuleTreeNode> = z.lazy(() =>
  z.object({
    attr: z.string(),
    carrier: CdtRuleCarrierSchema,
    describe: z.string(),
    direction: z.string(),
    ladderGroups: z.record(z.string(), z.string()).optional(),
    kids: z.array(
      z.object({
        value: z.string(),
        count: z.number().int().nonnegative(),
        node: CdtRuleTreeNodeSchema.nullable(),
      }),
    ),
  }),
);

export const CdtRuleNodeSchema = z.object({
  cdt0: z.string(),
  cdt1: z.string(),
  tree: CdtRuleTreeNodeSchema.nullable(),
});

export const CdtRuleSkuFactsSchema = z.object({
  skuId: z.string().min(1),
  cdt0: z.string(),
  cdt1: z.string(),
  section: z.string(),
  walkRank: z.number().int().nonnegative(),
  baseFacings: z.number().nonnegative(),
  attrs: z.record(z.string(), z.string()),
});

export const CdtRulePoolSchema = z.object({
  key: z.string(),
  cdt0: z.string(),
  cdt1: z.string(),
  host: z.string().optional(),
  members: z.array(z.string()),
});

export const CdtRuleCarveSchema = z.object({
  cdt0: z.string(),
  parentCdt0: z.string(),
  attr: z.string(),
  side: z.string(),
  sideOrder: z.number().int().nonnegative(),
});

export const CdtRulePoolingConstantsSchema = z.object({
  subFloorShare: z.number().nonnegative(),
  subFloorFacings: z.number().nonnegative(),
  oneSku: z.boolean(),
  topExempt: z.number().int().nonnegative(),
  cap: z.number().int().nonnegative(),
});

export const CdtZoneShareRuleSchema = z.object({
  sections: z.array(z.string()),
  share: z.number().nonnegative(),
  interleaveWith: z.enum(["left", "right"]).nullable().optional(),
});

export const CdtPartitionRulesSchema = z.object({
  zones: z.array(CdtZoneShareRuleSchema),
  warnings: z.array(z.string()),
});

export const CdtStratumPlanSchema = z.object({
  cdt0: z.string(),
  section: z.string(),
  bandAttr: z.string().nullable(),
  bandValue: z.string().nullable(),
  homeShelves: z.array(z.number().int().positive()),
  zone: z.number().int().nonnegative(),
  fullHeight: z.boolean(),
  anchors: z.boolean(),
  skuIds: z.array(z.string().min(1)),
});

export const CdtRuleRowHomeSchema = z.object({
  cdt0: z.string(),
  cdt1: z.string(),
  attr: z.string(),
  value: z.string(),
  shelves: z.array(z.number().int().positive()),
  skuIds: z.array(z.string().min(1)),
  shipped: z.boolean(),
});

/** Store-agnostic facts and decisions needed to materialize VR for child stores. */
export const CdtRuleSetSchema = z.object({
  cdt0Order: z.array(z.string()),
  frozenCdt0Names: z.array(z.string()),
  cdt1AttrOfCdt0: z.record(z.string(), z.string()),
  sectionOfCdt0: z.record(z.string(), z.string()),
  pairOrder: z.array(z.object({ cdt0: z.string(), cdt1: z.string() })),
  nodes: z.array(CdtRuleNodeSchema),
  skus: z.array(CdtRuleSkuFactsSchema),
  basePools: z.array(CdtRulePoolSchema),
  pooling: CdtRulePoolingConstantsSchema,
  carves: z.array(CdtRuleCarveSchema),
  baseShelfCm: z.number().nonnegative(),
  partition: CdtPartitionRulesSchema,
  strata: z.array(CdtStratumPlanSchema),
  baseDemandCm: z.record(z.string(), z.number().nonnegative()),
  minDemandCm: z.record(z.string(), z.number().nonnegative()),
  rowHomes: z.array(CdtRuleRowHomeSchema),
});

export const PostMerchflowCdtCompressionSchema = z.object({
  /** 16 is `schema_version VARCHAR(16)` on `merchflow_cdt_release`, not a taste. */
  schemaVersion: z.string().min(1).max(16),
  /**
   * The base pog the compression's measurements came from — and the switch for
   * the unresolved-combos guarantee.
   *
   * `null` SKIPS THAT CHECK, BY DESIGN. With no base pog there is nothing to
   * check placement against, so no combo can be shown unplaced and the
   * `unresolvedCombos` half of the 422 becomes unreachable. That is intended
   * rather than a hole: ranging beyond the base pog is legitimate, so this
   * gate is advisory, not a rule about what a correct CDT looks like. The gate
   * that decides whether a compression may ship without a base pog is the
   * studio's own finalize flow, not this contract and not the server.
   */
  basePogId: z.number().int().positive().nullable(),
  assignments: z.array(CdtCompressionAssignmentSchema).min(1),
  /**
   * CDT0 values in the order the compression MEASURED them. CDT0 has no
   * priority column anywhere, so `display_order` is the only place that order
   * can live — the server writes this array to it rather than alphabetising.
   *
   * AN ENTRY IS AN ORDERING HINT, NOT A NODE DECLARATION. It says where a CDT0
   * value sits relative to the others; it does not assert that the value
   * exists. The assignments are what declare which CDT0 values exist, and the
   * server treats them as the authority: an entry no assignment uses is
   * dropped, not materialised. Nothing is reported back when that happens, so
   * an extra is invisible rather than merely harmless — send the values the
   * assignments use.
   *
   * It is the order's PRIMARY record, not its only one: the server appends any
   * `cdt0` an assignment uses but this array omits, in first-seen order, so
   * `[]` degrades quietly to first-seen instead of failing. `[]` is reachable
   * — the studio builds this from the contract's CDT0 value order, which can
   * legitimately be absent.
   *
   * The two rules together mean the shipped level-0 nodes are exactly the
   * distinct `cdt0` values across `assignments`: this array only decides the
   * order they are written in, never the set.
   */
  cdt0Order: z.array(z.string().trim().min(1)),
  draft: z.unknown(),
  contract: z.unknown(),
  verification: z.unknown(),
  ruleSet: CdtRuleSetSchema,
  /**
   * Ship even though some CDT0/CDT1 combo stands nowhere on the base plan. The
   * check still runs and still reports; this only lets a human accept the result.
   * Never silent: the response returns `unplacedCombos`, the server logs, and the
   * release row records the acknowledgement.
   */
  acknowledgeUnplacedCombos: z.boolean().optional().default(false),
});

/**
 * A (cdt0, cdt1) combo with no placement, and the products that go unshelved.
 * The product codes are the point — a combo name alone is not something a user
 * can weigh.
 */
export const UnplacedCdtComboSchema = z.object({
  cdt0: z.string(),
  cdt1: z.string(),
  /**
   * Defaults to empty so a refusal from a server predating this field still
   * parses. Required here would break the one path whose job is explaining why
   * something was refused.
   */
  productCodes: z.array(z.string()).default([]),
});

export const ResponsePostMerchflowCdtCompressionSchema = z.object({
  treeId: z.number(),
  releaseId: z.number(),
  nodeCount: z.number(),
  itemCount: z.number(),
  /**
   * Combos shipped without a placement because the caller acknowledged them.
   * Empty on an ordinary ship; non-empty means those products have a CDT but no
   * shelf and will not be placed.
   */
  unplacedCombos: z.array(UnplacedCdtComboSchema),
});

export type ParamsMerchflowCdtCompression = z.infer<typeof ParamsMerchflowCdtCompressionSchema>;
export type CdtCompressionAssignment = z.infer<typeof CdtCompressionAssignmentSchema>;
export type CdtRuleCarrier = z.infer<typeof CdtRuleCarrierSchema>;
export type CdtRuleNode = z.infer<typeof CdtRuleNodeSchema>;
export type CdtRuleSkuFacts = z.infer<typeof CdtRuleSkuFactsSchema>;
export type CdtRulePool = z.infer<typeof CdtRulePoolSchema>;
export type CdtRuleCarve = z.infer<typeof CdtRuleCarveSchema>;
export type CdtRulePoolingConstants = z.infer<typeof CdtRulePoolingConstantsSchema>;
export type CdtZoneShareRule = z.infer<typeof CdtZoneShareRuleSchema>;
export type CdtPartitionRules = z.infer<typeof CdtPartitionRulesSchema>;
export type CdtStratumPlan = z.infer<typeof CdtStratumPlanSchema>;
export type CdtRuleRowHome = z.infer<typeof CdtRuleRowHomeSchema>;
export type CdtRuleSet = z.infer<typeof CdtRuleSetSchema>;
export type UnplacedCdtCombo = z.infer<typeof UnplacedCdtComboSchema>;
/**
 * The payload as a CALLER WRITES IT, so `z.input`: `acknowledgeUnplacedCombos`
 * defaults, and a client that has no opinion about it must be able to leave it
 * out. `z.infer` would be the post-parse shape, where the default has already
 * been applied and the field is required — correct for the server reading a
 * parsed body, wrong for every client building one.
 */
export type PostMerchflowCdtCompression = z.input<typeof PostMerchflowCdtCompressionSchema>;
export type ResponsePostMerchflowCdtCompression = z.infer<
  typeof ResponsePostMerchflowCdtCompressionSchema
>;

/**
 * 422 body. Two ways a compression is refused at the boundary, and neither is a
 * quiet field on a success response — the write is the last place a bad
 * compression can be stopped.
 *
 * The message key is `errorMessage`, matching `ApiError` in `api/apiError.ts`:
 * that is this package's convention, and it is what the shared `isApiError`
 * guard narrows on, so a consumer that already handles errors handles this one
 * too. A `message` key would be a third spelling beside the `{ error }` the
 * 404 and 409 branches use, and would be invisible to that guard.
 */
export const CdtCompressionRejectionSchema = z.object({
  errorMessage: z.string(),
  /**
   * Product codes the category does not contain. Non-empty means the
   * compression was handed the wrong universe — the shape of the defect that
   * put 232 LAUNDRY_AIDS products into a DETERGENT compression.
   */
  unmatchedProductCodes: z.array(z.string()),
  /**
   * (cdt0, cdt1) combos in the merchflow's assortment with no placement on the
   * base planogram, each with the products that will go unshelved. The client
   * gates on these too, but this one is the guarantee — overridable with
   * `acknowledgeUnplacedCombos`. Always empty when `basePogId` was `null`.
   */
  unresolvedCombos: z.array(UnplacedCdtComboSchema),
});

export type CdtCompressionRejection = z.infer<typeof CdtCompressionRejectionSchema>;
